import Vue from "vue"
import errors from "@/data/errors"
import errorLogger from "@/utils/errorLogger"
import store from "@/store"

// const ROOT = process.env.VUE_APP_API_ROUTE
const TIMEOUT = process.env.NODE_ENV === "production" ? 7000 : 30000
const ROOT = process.env.VUE_APP_API_ROUTE || "http://localhost:3000"

const errorConfig = {
  title: "Error",
  type: "error",
  duration: 5000
}

const timeout = (request, time = TIMEOUT) => {
  return new Promise((resolve, reject) => {
    setTimeout(() => reject(new Error("The request timed out.")), time)
    request.then(resolve, reject)
  })
}

function queryBuilder(args) {
  if (!args) return ""

  const argsStr = Object.entries(args)
    .filter(arg => arg[1] !== undefined) // filter out undefined
    .map(arg => arg.join("="))
    .join("&")

  return "?" + argsStr
}

const http = {
  get(path, options = {}) {
    const token = localStorage.getItem("mtx-token")
    if (token) {
      options.headers = {
        ...options.headers,
        Authorization: `Bearer ${token}`
      }
    }

    const { args } = options
    delete options.args

    const query = queryBuilder(args)
    return timeout(
      fetch(`${ROOT}${path}${query}`, options)
        .then(async res => {
          const { status } = res

          if (status === 401) {
            store.dispatch("logout")
          }

          const json = await res.json()

          if (!json.success) {
            const error = new Error(`Error fetching ${ROOT}${path}${query}`)
            if (json.error) {
              error.name = json.error.name
              error.message += `:\n${json.error.message}`
            }
            errorLogger.log(error)

            Vue.notify({
              ...errorConfig,
              text: errors.API_UNKNOWN
            })
          }

          return { ...json, status }
        })
        .catch(error => {
          errorLogger.log(error)
          Vue.notify({
            ...errorConfig,
            text:
              "Looks like there is an error with your network connection. Please check your connection settings."
          })

          return {}
        })
    ).catch(error => {
      errorLogger.log(error)
      Vue.notify({
        ...errorConfig,
        text: "The request took too long to process."
      })

      return {}
    })
  },

  post(path, options) {
    return http.get(path, { ...options, method: "POST" })
  },

  patch(path, options) {
    return http.get(path, { ...options, method: "PATCH" })
  }
}

export default {
  getArtifacts() {
    return http.get("/artifacts")
  },

  uploadFiles(files) {
    const body = new FormData()
    Object.keys(files).forEach(key => {
      body.append("files", files[key])
    })

    return fetch(`${ROOT}/upload/files`, {
      method: "POST",
      body
    }).then(res => res.json())
  },

  uploadJson(obj) {
    return fetch(`${ROOT}/upload/json`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(obj)
    }).then(res => res.json())
  },

  //
  // User
  //
  requestSecret() {
    return http.get(`/users/secret`)
  },

  login({ owner, signature, token }) {
    return http.post(`/users/login`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ owner, signature })
    })
  },

  me() {
    const token = localStorage.getItem("mtx-token")
    if (!token) {
      return Promise.resolve({ success: true, data: null })
    }

    return http.get(`/users/me`)
  },

  userNotifications() {
    return http.get(`/users/me/notifications`)
  },

  editUser(edits) {
    const token = localStorage.getItem("mtx-token")
    if (!token) {
      return Promise.resolve({ success: true, data: null })
    }

    return http.patch(`/users/me`, {
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ edits })
    })
  },

  //
  // Tournaments
  //
  getTournaments(args) {
    return http.get(`/tournaments`, { args })
  },

  getTournament(tournamentAddress) {
    return http.get(`/tournaments/${tournamentAddress}`)
  },

  getTournamentRound(tournamentAddress, roundIndex) {
    return http.get(`/tournaments/${tournamentAddress}/round/${roundIndex}`)
  },

  //
  // Submissions
  //
  getSubmission(submissionHash) {
    return http.get(`/submissions/${submissionHash}`)
  },

  getSubmissionsByOwner(params) {
    const { owner, offset, count } = params
    return http.get(`/submissions/owner/${owner}`, {
      args: { offset, count }
    })
  },

  //
  // Commits
  //
  getCommit(commitHash) {
    return http.get(`/commits/${commitHash}`)
  },

  getCommitRewards(userAddress) {
    return http.get(`/commits/rewards/${userAddress}`)
  },

  getCommitsByOwner(params) {
    const { owner, offset, count } = params
    return http.get(`/commits/owner/${owner}`, {
      args: { offset, count }
    })
  },

  //
  // Comments
  //
  getComments({ referenceType, referenceId }) {
    return http.get(`/comments`, {
      args: {
        referenceType,
        referenceId
      }
    })
  },

  postComment(comment) {
    return http.post(`/comments`, {
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(comment)
    })
  }
}
